// src/pages/CarListingPage.tsx

import { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Stack,
  CircularProgress,
  Pagination,
  Chip,
  Divider,
  FormControlLabel,
  Switch,
} from '@mui/material';
import CarCard from '../components/CarCard';
import CarFilterBar from '../components/CarFilterBar';
import axiosInstance from '../services/axiosInstance';
import { decodeId } from '../utils/idEncoder';

interface HomePageProps {
  mode: 'light' | 'dark';
  setMode: (mode: 'light' | 'dark') => void;
}

const CarListingPage = ({ mode, setMode }: HomePageProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  const idListKeys = [
    'vehicleTransmissionTypeIds',
    'vehicleFuelTypeIds',
    'vehicleBrandIds',
    'vehicleBodyTypeIds',
    'vehicleClassIds',
    'dealerIds'
  ];

  const parseQueryParam = (key: string): number[] => {
    const raw = searchParams.get(key);
    if (!raw) return [];

    const parts = raw.split(',');

    if (idListKeys.includes(key)) {
      return parts
        .map(hash => decodeId(hash))
        .filter((id): id is number => id !== null);
    }

    return parts.map(Number);
  };

  const vehicleTransmissionTypeIds = parseQueryParam('vehicleTransmissionTypeIds');
  const vehicleFuelTypeIds = parseQueryParam('vehicleFuelTypeIds');
  const vehicleBrandIds = parseQueryParam('vehicleBrandIds');
  const vehicleBodyTypeIds = parseQueryParam('vehicleBodyTypeIds');
  const vehicleClassIds = parseQueryParam('vehicleClassIds');
  const dealerIds = parseQueryParam('dealerIds');

  const statusIdString = searchParams.get('statusId');
  const statusID = statusIdString ? statusIdString.split(',') : [];

  const parsePriceParams = () => {
    const min = searchParams.get('minAskingPrice');
    const max = searchParams.get('maxAskingPrice');
    return {
      minAskingPrice: min ? Number(min) : null,
      maxAskingPrice: max ? Number(max) : null,
    };
  };
  const { minAskingPrice, maxAskingPrice } = parsePriceParams();

  const parseYearParams = () => {
    const min = searchParams.get('minManufacturingYear');
    const max = searchParams.get('maxManufacturingYear');
    return {
      minManufacturingYear: min ? Number(min) : null,
      maxManufacturingYear: max ? Number(max) : null,
    };
  };
  const { minManufacturingYear, maxManufacturingYear } = parseYearParams();

  const parseMileageParams = () => {
    const min = searchParams.get('minMileage');
    const max = searchParams.get('maxMileage');
    return {
      minMileage: min ? Number(min) : null,
      maxMileage: max ? Number(max) : null,
    };
  };
  const { minMileage, maxMileage } = parseMileageParams();

  const parseDepreciationParams = () => {
    const min = searchParams.get('minDepreciation');
    const max = searchParams.get('maxDepreciation');
    return {
      minDepreciationValue: min ? Number(min) : null,
      maxDepreciationValue: max ? Number(max) : null,
    };
  };
  const { minDepreciationValue, maxDepreciationValue } = parseDepreciationParams();

  const vehicleModel = searchParams.get('vehicleModel') || '';
  const initialSearchQuery = searchParams.get('searchKeyword') || '';

  // --- STATE ---
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [vehicleList, setVehicleList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [currentPage, setCurrentPage] = useState(1);
  const [racingAd, setRacingAd] = useState<boolean | null>(null);

  // NEW: Pagination states driven by the backend
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // ✅ ALWAYS display 10 items per page
  const itemsPerPage = 10;

  // --- FETCH DATA ---
  useEffect(() => {
    const fetchVehicles = async () => {
      setLoading(true);
      try {
        const payload: any = {
          vehicleTransmissionTypeIds,
          vehicleFuelTypeIds,
          vehicleBrandIds,
          vehicleClassIds,
          vehicleBodyTypeIds,
          minAskingPrice,
          maxAskingPrice,
          minManufacturingYear,
          maxManufacturingYear,
          minMileage,
          maxMileage,
          minDepreciationValue,
          maxDepreciationValue,
          statusID,
          dealerIds,

          // Backend Pagination & Search parameters
          pageNumber: currentPage,
          pageSize: itemsPerPage,
          searchKeyword: searchQuery,
          vehicleModel: vehicleModel
        };

        if (racingAd) {
          payload.racingAd = true;
        }

        const response = await axiosInstance.post(
          "/CustomerVehicle/VehicleGetListPublished",
          payload,
        );

        const result = response.data as {
          responseCode: string;
          responseMessage?: string;
          responseData?: any;
        };

        if (result.responseCode === "00" && result.responseData) {
          const data = result.responseData;
          setVehicleList(data.vehicleList || data.VehicleList || []);

          // ✅ Map new backend pagination data to state safely (checking camelCase and PascalCase)
          setTotalRecords(data.totalRecords ?? data.TotalRecords ?? 0);
          setTotalPages(data.totalPages ?? data.TotalPages ?? 0);
        } else {
          console.error("Failed to load vehicles:", result.responseMessage);
          setVehicleList([]);
          setTotalRecords(0);
          setTotalPages(0);
        }
      } catch (err) {
        console.error("Error fetching vehicles", err);
      } finally {
        setLoading(false);
      }
    };

    fetchVehicles();
  }, [location.search, racingAd, currentPage, itemsPerPage, searchQuery]);

  // Reset to page 1 if search params change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, viewMode, location.search]);

  // --- HANDLER: FILTER CHANGE ---
  const handleFilterChange = (key: string, value: string) => {
    const currentParams = new URLSearchParams(location.search);

    if (value) {
      currentParams.set(key, value);
    } else {
      currentParams.delete(key);
    }

    currentParams.delete('page');
    navigate(`/vehicle-listing?${currentParams.toString()}`);
  };

  const vehicleClassMap: Record<number, string> = {
    2: 'Light Commercial',
    3: 'Heavy Commercial',
  };

  const handleRemoveVehicleClass = (idToRemove: number) => {
    const currentParams = new URLSearchParams(location.search);
    const existing = currentParams.get('vehicleClassIds');

    if (!existing) return;

    const parts = existing.split(',');
    const updatedParts = parts.filter(hash => {
      const decoded = decodeId(hash);
      return decoded !== idToRemove;
    });

    if (updatedParts.length > 0) {
      currentParams.set('vehicleClassIds', updatedParts.join(','));
    } else {
      currentParams.delete('vehicleClassIds');
    }

    navigate(`/vehicle-listing?${currentParams.toString()}`);
  };

  // Helper for pagination text display
  const indexOfFirstRecord = (currentPage - 1) * itemsPerPage + 1;
  const indexOfLastRecord = Math.min(currentPage * itemsPerPage, totalRecords);

  return (
    <>
      <Header />
      <Box sx={{ px: 4, py: 6, mt: 5, minHeight: '80vh' }}>
        <Box display="flex" justifyContent="center">
          <Box sx={{ width: '100%', maxWidth: 1200 }}>


            {/* Top Bar */}
            <Box
              sx={{
                p: { xs: 2.5, md: 3.5 },
                mb: 5,
                borderRadius: 4,
                bgcolor: 'background.paper',
                boxShadow: '0 8px 32px rgba(0,0,0,0.04)',
                border: '1px solid',
                borderColor: 'rgba(0,0,0,0.02)',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -50,
                  right: -50,
                  width: 150,
                  height: 150,
                  background: 'radial-gradient(circle, rgba(255,161,0,0.05) 0%, rgba(255,255,255,0) 70%)',
                  borderRadius: '50%',
                  pointerEvents: 'none',
                }}
              />

              <CarFilterBar
                viewMode={viewMode}
                onToggleViewMode={() => setViewMode(prev => (prev === 'grid' ? 'list' : 'grid'))}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                onFilterChange={handleFilterChange}
              />

              <Divider sx={{ my: 3, borderStyle: 'dashed', borderColor: '#e0e0e0' }} />

              {/* Dynamic Filter Controls & Racing Ad Switch */}
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>

                {/* ✅ Left Side: Record Count & Active Vehicle Classes */}
                <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>

                  {/* Total Vehicles Record Count (Your requested blue box area) */}
                  <Typography
                    color="text.primary"
                    sx={{
                      fontWeight: 800,
                      letterSpacing: 0.5,
                      borderRight: vehicleClassIds.length > 0 ? '2px solid #eee' : 'none',
                      pr: vehicleClassIds.length > 0 ? 2 : 0
                    }}
                  >
                    {totalRecords} {totalRecords === 1 ? 'Vehicle' : 'Vehicles'} Found
                  </Typography>

                  {/* Active Vehicle Classes */}
                  {vehicleClassIds.length > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        bgcolor: '#f8f9fa',
                        py: 0.75,
                        px: 1.5,
                        borderRadius: 2
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Active:
                      </Typography>
                      {vehicleClassIds.map((id) => (
                        <Chip
                          key={id}
                          label={vehicleClassMap[id] || `Class ${id}`}
                          onDelete={() => handleRemoveVehicleClass(id)}
                          color="primary"
                          size="small"
                          sx={{
                            fontWeight: 600,
                            borderRadius: 1.5,
                            bgcolor: 'primary.light',
                            color: 'primary.dark',
                            '& .MuiChip-deleteIcon': { color: 'primary.dark' }
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Right Side: POWERFUL Racing Ads Toggle */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!racingAd}
                      onChange={(e) => setRacingAd(e.target.checked ? true : null)}
                      color="warning"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#FF9800',
                          '& + .MuiSwitch-track': {
                            backgroundColor: '#FF9800',
                            opacity: 0.5,
                          },
                        },
                      }}
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography
                        variant="button"
                        sx={{
                          fontWeight: 800,
                          color: racingAd ? '#E65100' : 'text.secondary',
                          letterSpacing: 0.5
                        }}
                      >
                        Racing Ads
                      </Typography>
                      {racingAd && (
                        <Box component="span" sx={{ fontSize: '1.2rem', lineHeight: 1 }}>⚡</Box>
                      )}
                    </Box>
                  }
                  sx={{
                    ml: 'auto',
                    pl: 2,
                    pr: 2.5,
                    py: 0.75,
                    border: '1px solid',
                    borderColor: racingAd ? 'warning.main' : '#eee',
                    background: racingAd
                      ? 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)'
                      : '#fafafa',
                    boxShadow: racingAd ? '0 4px 12px rgba(255, 152, 0, 0.2)' : 'none',
                    borderRadius: 12,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: racingAd
                        ? 'linear-gradient(135deg, #ffe0b2 0%, #ffcc80 100%)'
                        : '#f5f5f5',
                      transform: 'translateY(-1px)',
                    }
                  }}
                />
              </Box>
            </Box>

            {/* Loading State */}
            {loading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={200}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                {viewMode === 'grid' ? (
                  <Stack spacing={2} mt={2}>
                    <Grid container spacing={2}>
                      {/* Map directly from vehicleList */}
                      {vehicleList.map((vehicle: any) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={vehicle.vehicleId}>
                          <CarCard vehicle={vehicle} viewMode="grid" />
                        </Grid>
                      ))}
                    </Grid>
                  </Stack>
                ) : (
                  <Stack spacing={2} mt={2}>
                    {/* Map directly from vehicleList */}
                    {vehicleList.map(vehicle => (
                      <CarCard
                        key={vehicle.vehicleId}
                        vehicle={vehicle}
                        viewMode="list"
                      />
                    ))}
                  </Stack>
                )}

                {/* Pagination */}
                {vehicleList.length > 0 ? (
                  <Box
                    mt={4}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                  >
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      Showing {indexOfFirstRecord} to {indexOfLastRecord} of {totalRecords} Vehicles
                    </Typography>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={(_, page) => setCurrentPage(page)}
                      color="primary"
                    />
                  </Box>
                ) : (
                  <Box mt={4} textAlign="center">
                    <Typography color="text.secondary">No vehicles found.</Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
      <Footer />
    </>
  );
};

export default CarListingPage;